import { useMutation, useQueryClient } from '@tanstack/react-query';
import { growthKeys } from '@/data/queryKeys';
import { useGrowthQuery } from '@/data/useGrowthQuery';
import { useNotify } from '@/data/notifications';
import {
  claimCitationListing,
  createCitationListing,
  fetchCitations,
  helpResolveCitation,
  pushCitationCorrection,
  suggestMoreCitations,
} from '../api';

export function useCitations() {
  return useGrowthQuery({
    queryKey: growthKeys.citations(),
    queryFn: () => fetchCitations(),
  });
}

export function useCitationMutations() {
  const queryClient = useQueryClient();
  const notify = useNotify();

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: growthKeys.citations() });

  const suggestMore = useMutation({
    mutationFn: suggestMoreCitations,
    onSuccess: () => {
      notify.success('Citation suggestions requested');
      invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const createListing = useMutation({
    mutationFn: createCitationListing,
    onSuccess: () => {
      notify.success('Create listing requested');
      invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const claimListing = useMutation({
    mutationFn: claimCitationListing,
    onSuccess: () => {
      notify.success('Claim listing requested');
      invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const helpResolve = useMutation({
    mutationFn: helpResolveCitation,
    onSuccess: () => {
      notify.success('Help resolve requested');
      invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const correctionPush = useMutation({
    mutationFn: pushCitationCorrection,
    onSuccess: () => {
      notify.success('Correction push requested');
      invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const pendingDirectoryKey =
    createListing.isPending
      ? createListing.variables
      : claimListing.isPending
        ? claimListing.variables
        : helpResolve.isPending
          ? helpResolve.variables
          : correctionPush.isPending
            ? correctionPush.variables
            : undefined;

  return {
    suggestMore,
    createListing,
    claimListing,
    helpResolve,
    correctionPush,
    pendingDirectoryKey,
  };
}
